/**
 * The "Give feedback" dialog: one form for every entry.
 *
 * Five types (something broke, wrong result, idea, change, question); each asks only what
 * matters (docs/feedback.md section 4). The entry decides what is prefilled: the type,
 * the topic of a wrong result, the case and the line. Everything the browser cannot
 * know is attached by the server; the folded "What we attach" block says what that is.
 *
 * Saving writes one row to the app database (`POST /feedback`). Nothing is sent to
 * anybody: no mail, no Teams message, no ticket.
 *
 * Decision D7 (19 Oct 2026): typed text is never lost by a stray click. On a case the form
 * keeps its unsent text in this browser (`feedback-draft-store.ts`) and brings it back on
 * the next opening. Cancel, Escape, the close button and a click beside the dialog ask
 * first while the form holds typed text: keep writing, close and keep the text (on a
 * case), or discard it.
 */

import { Icon, MessageSquarePlus } from "@norican/icons";
import {
  Alert,
  Button,
  Checkbox,
  Collapsible,
  Dialog,
  Field,
  Input,
  Radio,
  RadioGroup,
  Select,
  Textarea,
  Toggle,
  ToggleGroup,
  Typography,
  useToastManager,
} from "@norican/react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import {
  buildFeedbackRequest,
  bundleVersion,
  clientContext,
  emptyFeedbackForm,
  FEEDBACK_FREQUENCIES,
  FEEDBACK_IMPACTS,
  FEEDBACK_TEXT_MAX,
  FEEDBACK_TITLE_MAX,
  type FeedbackFormState,
  type FeedbackFrequency,
  feedbackHubFailureText,
  type FeedbackHubTopic,
  type FeedbackImpact,
  type FeedbackItem,
  type FeedbackKind,
  type FeedbackSurface,
  useCreateFeedback,
  useMe,
  validateFeedbackForm,
} from "@/lib/api";
import { hasCapability, useAreaLabels, useMyAreas } from "@/lib/areas";
import { logger } from "@/lib/logger";
import { useDialogUsage } from "@/lib/usage-react";

import {
  clearFeedbackDraft,
  hasTypedText,
  keepFeedbackDraft,
  readFeedbackDraft,
} from "./feedback-draft-store";
import {
  DRAFT_WORDS,
  FREQUENCY_LABEL,
  IMPACT_LABEL,
  INTENT_OPTIONS,
  KIND_QUESTIONS,
  KIND_TILES,
  MAIL_TEXT_HINT,
  MODEL_FIX_NOTE,
  NO_MAIL_IN_TEXT_HINT,
  NOTHING_LEAVES_NOTE,
  RIGHT_LABEL,
  SAVED_TOAST,
  SURFACE_LABEL,
  TOPIC_LABEL,
  topicOptions,
} from "./labels";

/** What an entry hands the dialog. */
export interface GiveFeedbackOptions {
  surface: FeedbackSurface;
  caseId?: string | null;
  /** Position of the line in the stored draft (0-based). */
  lineIndex?: number | null;
  /** Translation feedback: the language the mail was translated into. */
  targetLanguage?: string | null;
  kind?: FeedbackKind | null;
  topic?: FeedbackHubTopic | null;
  /** Called with the saved item. */
  onSaved?: (item: FeedbackItem) => void;
}

export interface FeedbackFormProps {
  target: GiveFeedbackOptions;
  /** Start values (tests and prefilled entries); the type and topic come from `target`. */
  initial?: Partial<FeedbackFormState>;
  /** Closes the dialog for good (after the ask, when the form holds typed text). */
  onCancel: () => void;
  onSaved: (item: FeedbackItem) => void;
  /**
   * The ask before typed text is lost, when the dialog owns it (Escape and a click beside
   * the dialog open it too). Without these props the form keeps the ask itself.
   */
  asking?: boolean;
  onAskingChange?: (asking: boolean) => void;
  /** Tells the dialog whether the form holds typed text now. */
  onTypedChange?: (typed: boolean) => void;
}

/** The plain-word lines of "What we attach". */
export function attachedContext(
  target: GiveFeedbackOptions,
  route: string,
  includeMailText: boolean,
): string[] {
  const lines = [`Page: ${route || "/"}`, `Where: ${SURFACE_LABEL[target.surface]}`];
  if (target.caseId) {
    lines.push(`Case: ${target.caseId}`);
    if (typeof target.lineIndex === "number" && target.lineIndex >= 0) {
      lines.push(`Line: ${target.lineIndex + 1}`);
    }
    lines.push("What I2Q proposed on the case and what sales already did on it");
    lines.push(
      includeMailText
        ? "The mail text (first 8000 characters)"
        : "Without the mail text (not ticked)",
    );
  }
  lines.push("Your area and your roles");
  lines.push(`App version: ${bundleVersion()}`);
  lines.push("Browser, language, window size and time zone");
  return lines;
}

function TextQuestionField({
  label,
  required,
  value,
  error,
  onChange,
}: {
  label: string;
  required: boolean;
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} required={required} error={error}>
      <Textarea
        size="sm"
        rows={required ? 3 : 2}
        value={value}
        maxLength={FEEDBACK_TEXT_MAX}
        onChange={(event) => onChange(event.target.value)}
        aria-label={label}
      />
    </Field>
  );
}

/** The "what is right" field, shaped by the topic. */
function RightValueField({
  topic,
  form,
  errors,
  setRight,
  setField,
}: {
  topic: FeedbackHubTopic;
  form: FeedbackFormState;
  errors: Record<string, string>;
  setRight: (key: keyof FeedbackFormState["right"], value: string) => void;
  setField: (key: string, value: string) => void;
}) {
  const label = RIGHT_LABEL[topic];
  const hint = "Optional, but it makes the example useful.";
  switch (topic) {
    case "customer":
      return (
        <Field label={label} hint={hint} error={errors["right.account"]}>
          <Input
            value={form.right.account ?? ""}
            maxLength={40}
            onChange={(event) => setRight("account", event.target.value)}
            aria-label={label}
          />
        </Field>
      );
    case "item":
      return (
        <Field label={label} hint={hint} error={errors["right.item"]}>
          <Input
            value={form.right.item ?? ""}
            maxLength={60}
            onChange={(event) => setRight("item", event.target.value)}
            aria-label={label}
          />
        </Field>
      );
    case "quantity":
      return (
        <Field label={label} hint={hint} error={errors["right.quantity"]}>
          <Input
            inputMode="decimal"
            value={form.right.quantity ?? ""}
            onChange={(event) => setRight("quantity", event.target.value)}
            aria-label={label}
          />
        </Field>
      );
    case "price":
      return (
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          <Field label={label} hint={hint} error={errors["right.unit_price"]}>
            <Input
              inputMode="decimal"
              value={form.right.unit_price ?? ""}
              onChange={(event) => setRight("unit_price", event.target.value)}
              aria-label={label}
            />
          </Field>
          <Field label="Currency" error={errors["right.currency"]}>
            <Input
              value={form.right.currency ?? ""}
              maxLength={3}
              onChange={(event) => setRight("currency", event.target.value)}
              aria-label="Currency"
            />
          </Field>
        </div>
      );
    case "classification":
      return (
        <RadioGroup
          legend={label}
          value={form.right.intent ?? ""}
          onValueChange={(value) => setRight("intent", String(value))}
          className="flex flex-col gap-1.5"
        >
          {INTENT_OPTIONS.map((option) => (
            <Radio.Root key={option.value} value={option.value} label={option.label} />
          ))}
        </RadioGroup>
      );
    default:
      return (
        <TextQuestionField
          label={label}
          required={false}
          value={form.fields.right ?? ""}
          error={errors.right}
          onChange={(value) => setField("right", value)}
        />
      );
  }
}

/** The form inside the dialog. Rendered on its own by the tests. */
export function FeedbackForm({
  target,
  initial,
  onCancel,
  onSaved,
  asking: askingProp,
  onAskingChange,
  onTypedChange,
}: FeedbackFormProps) {
  const location = useLocation();
  const { data: me } = useMe();
  const myAreas = useMyAreas();
  const areaLabels = useAreaLabels();
  const create = useCreateFeedback();
  const seeMargin = hasCapability(me, "see_margin");
  const caseId = target.caseId ?? null;
  const surface = target.surface;
  const lineIndex = typeof target.lineIndex === "number" ? target.lineIndex : null;

  // The unsent text of this place (case, surface, line) from an earlier opening, read once
  // per opening. The type and topic the entry sets win over the kept ones (review of 19 Oct
  // 2026): a kept text never moves its feedback to another kind or topic.
  const [restored] = useState(() => readFeedbackDraft({ caseId, surface, lineIndex }));
  const [form, setForm] = useState<FeedbackFormState>(() => {
    const start = { ...emptyFeedbackForm(target.kind ?? null, target.topic ?? null), ...restored };
    if (target.kind) {
      const keptTopic = restored?.kind === target.kind ? restored.topic : null;
      start.kind = target.kind;
      start.topic = target.kind === "model" ? (target.topic ?? keptTopic) : null;
    }
    return { ...start, ...initial };
  });
  const [tried, setTried] = useState(false);
  const [prefilledKind] = useState(Boolean(target.kind));
  const [pickingKind, setPickingKind] = useState(!(restored?.kind ?? target.kind));
  const [restoredShown, setRestoredShown] = useState(restored !== null);
  const [ownAsking, setOwnAsking] = useState(false);
  const asking = askingProp ?? ownAsking;
  const setAsking = onAskingChange ?? setOwnAsking;
  const typed = hasTypedText(form);

  // Keep the typed text for this case while the dialog is open; forget it once empty.
  useEffect(() => {
    keepFeedbackDraft({ caseId, surface, lineIndex }, form);
  }, [caseId, surface, lineIndex, form]);
  useEffect(() => {
    onTypedChange?.(typed);
  }, [onTypedChange, typed]);

  /** Cancel: close at once without typed text, else ask first. */
  const cancel = () => {
    if (typed) {
      logger.debug("[feedback] cancel asks first", { case: caseId });
      setAsking(true);
      return;
    }
    onCancel();
  };
  const discard = () => {
    logger.debug("[feedback] text discarded", { case: caseId });
    clearFeedbackDraft({ caseId, surface, lineIndex });
    onCancel();
  };
  const startAgain = () => {
    logger.debug("[feedback] start again", { case: caseId });
    clearFeedbackDraft({ caseId, surface, lineIndex });
    setForm(emptyFeedbackForm(target.kind ?? null, target.topic ?? null));
    setPickingKind(!target.kind);
    setRestoredShown(false);
    setTried(false);
  };

  const errors = validateFeedbackForm(form, { seeMargin });
  const shownErrors = tried ? errors : {};
  const failure = create.error ? feedbackHubFailureText(create.error, "create") : null;
  const hasCase = Boolean(target.caseId);
  const kind = form.kind;

  // Without a case, a person with several areas may say which one the feedback is about.
  const areaChoices: { code: string; label: string }[] = hasCase
    ? []
    : myAreas.all
      ? areaLabels.areas.map((area) => {
          const code = (area.role ?? area.code).toLowerCase();
          return { code, label: areaLabels.label(code) };
        })
      : myAreas.areas.length > 1
        ? myAreas.areas.map((code) => ({ code, label: areaLabels.label(code) }))
        : [];

  const update = (patch: Partial<FeedbackFormState>) => setForm((prev) => ({ ...prev, ...patch }));
  const setField = (key: string, value: string) =>
    setForm((prev) => ({ ...prev, fields: { ...prev.fields, [key]: value } }));
  const setRight = (key: keyof FeedbackFormState["right"], value: string) =>
    setForm((prev) => ({ ...prev, right: { ...prev.right, [key]: value } }));

  const save = () => {
    setTried(true);
    if (Object.keys(errors).length > 0) {
      logger.debug("[feedback] form not complete", Object.keys(errors));
      return;
    }
    let request;
    try {
      request = buildFeedbackRequest(form, target, {
        route: location.pathname,
        client: clientContext(),
        seeMargin,
      });
    } catch (error) {
      logger.warn("[feedback] request not built", error);
      return;
    }
    logger.debug("[feedback] create", {
      kind: request.kind,
      topic: request.topic,
      surface: request.surface,
      case: request.case_id,
      line: request.line_index,
    });
    create.mutate(request, {
      onSuccess: (item) => {
        logger.debug("[feedback] saved", { id: item?.feedback_id });
        clearFeedbackDraft({ caseId, surface, lineIndex });
        onSaved(item);
      },
      onError: (error) => logger.warn("[feedback] save failed", error),
    });
  };

  const activeTile = KIND_TILES.find((tile) => tile.kind === kind);

  return (
    <div className="flex flex-col gap-4">
      {restoredShown && (
        <div className="flex flex-wrap items-center gap-2" data-feedback-restored>
          <Typography variant="caption" tone="muted">
            {DRAFT_WORDS.restored}
          </Typography>
          <Button size="sm" variant="ghost" tone="neutral" onClick={startAgain}>
            {DRAFT_WORDS.startAgain}
          </Button>
        </div>
      )}
      {pickingKind || !kind ? (
        <RadioGroup
          legend="What kind of feedback is it?"
          value={kind ?? ""}
          onValueChange={(value) => {
            const next = String(value) as FeedbackKind;
            logger.debug("[feedback] type", next);
            update({ kind: next, topic: next === "model" ? form.topic : null });
            setPickingKind(false);
          }}
          className="flex flex-col gap-2"
        >
          {KIND_TILES.map((tile) => (
            <Radio.Root
              key={tile.kind}
              value={tile.kind}
              label={
                <span className="flex flex-col">
                  <span className="text-fg text-sm font-semibold">{tile.title}</span>
                  <span className="text-fg-muted text-xs">{tile.hint}</span>
                </span>
              }
            />
          ))}
        </RadioGroup>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="body-sm" className="text-fg font-semibold">
            {activeTile?.title}
          </Typography>
          <Button size="sm" variant="ghost" tone="neutral" onClick={() => setPickingKind(true)}>
            {prefilledKind ? "Pick another type" : "Change type"}
          </Button>
        </div>
      )}
      {shownErrors.kind && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          {shownErrors.kind}
        </Typography>
      )}

      {kind && !pickingKind && (
        <>
          <Typography variant="caption" tone="muted">
            {NO_MAIL_IN_TEXT_HINT}
          </Typography>

          {kind === "model" && (
            <Field label="What was wrong?" required error={shownErrors.topic}>
              <Select.Root
                value={form.topic ?? ""}
                onValueChange={(value) => {
                  const next = typeof value === "string" && value !== "" ? value : null;
                  update({ topic: next as FeedbackHubTopic | null });
                }}
              >
                <Select.Trigger size="sm" aria-label="What was wrong?" className="w-full">
                  <Select.Value>
                    {(value: unknown) =>
                      typeof value === "string" && value in TOPIC_LABEL
                        ? TOPIC_LABEL[value as FeedbackHubTopic]
                        : "Pick a topic"
                    }
                  </Select.Value>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Positioner>
                    <Select.Popup>
                      <Select.List>
                        {topicOptions(seeMargin).map((topic) => (
                          <Select.Item key={topic} value={topic}>
                            {TOPIC_LABEL[topic]}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Popup>
                  </Select.Positioner>
                </Select.Portal>
              </Select.Root>
            </Field>
          )}

          {kind === "model" && KIND_QUESTIONS.model[0] && (
            <TextQuestionField
              label={KIND_QUESTIONS.model[0].label}
              required={false}
              value={form.fields.wrong ?? ""}
              error={shownErrors.wrong}
              onChange={(value) => setField("wrong", value)}
            />
          )}
          {kind === "model" && form.topic && (
            <RightValueField
              topic={form.topic}
              form={form}
              errors={shownErrors}
              setRight={setRight}
              setField={setField}
            />
          )}
          {(kind === "model" ? KIND_QUESTIONS.model.slice(1) : KIND_QUESTIONS[kind]).map(
            (question) => (
              <TextQuestionField
                key={question.key}
                label={question.label}
                required={question.required}
                value={form.fields[question.key] ?? ""}
                error={shownErrors[question.key]}
                onChange={(value) => setField(question.key, value)}
              />
            ),
          )}

          {kind === "idea" && (
            <Field label="How often does this get in your way?">
              <ToggleGroup.Root
                size="sm"
                value={form.frequency ? [form.frequency] : []}
                onValueChange={(value) => {
                  const picked = (value as string[])[0] as FeedbackFrequency | undefined;
                  update({ frequency: picked ?? null });
                }}
                aria-label="How often does this get in your way?"
                className="flex-wrap"
              >
                {FEEDBACK_FREQUENCIES.map((value) => (
                  <Toggle key={value} value={value} size="sm">
                    {FREQUENCY_LABEL[value]}
                  </Toggle>
                ))}
              </ToggleGroup.Root>
            </Field>
          )}

          <Field label="How much does this affect your work?">
            <ToggleGroup.Root
              size="sm"
              value={form.impact ? [form.impact] : []}
              onValueChange={(value) => {
                const picked = (value as string[])[0] as FeedbackImpact | undefined;
                update({ impact: picked ?? null });
              }}
              aria-label="How much does this affect your work?"
              className="flex-wrap"
            >
              {FEEDBACK_IMPACTS.map((value) => (
                <Toggle key={value} value={value} size="sm">
                  {IMPACT_LABEL[value]}
                </Toggle>
              ))}
            </ToggleGroup.Root>
          </Field>

          <Field
            label="Short title (optional)"
            hint="We make one from your text when you leave it empty."
            error={shownErrors.title}
          >
            <Input
              value={form.title}
              maxLength={FEEDBACK_TITLE_MAX}
              onChange={(event) => update({ title: event.target.value })}
              aria-label="Short title"
            />
          </Field>

          {areaChoices.length > 0 && (
            <Field label="Which area is this about? (optional)">
              <Select.Root
                value={form.area ?? ""}
                onValueChange={(value) =>
                  update({ area: typeof value === "string" && value !== "" ? value : null })
                }
              >
                <Select.Trigger size="sm" aria-label="Area" className="w-full">
                  <Select.Value>
                    {(value: unknown) =>
                      areaChoices.find((choice) => choice.code === value)?.label ?? "No area"
                    }
                  </Select.Value>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Positioner>
                    <Select.Popup>
                      <Select.List>
                        <Select.Item value="">No area</Select.Item>
                        {areaChoices.map((choice) => (
                          <Select.Item key={choice.code} value={choice.code}>
                            {choice.label}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Popup>
                  </Select.Positioner>
                </Select.Portal>
              </Select.Root>
            </Field>
          )}

          {hasCase && (
            <div className="flex flex-col gap-1">
              <Checkbox.Root
                checked={form.includeMailText}
                onCheckedChange={(checked) => update({ includeMailText: checked === true })}
                label="Include the mail text"
              />
              <Typography variant="caption" tone="subtle">
                {MAIL_TEXT_HINT}
              </Typography>
            </div>
          )}

          {kind === "model" && (
            <Alert tone="info" title="This does not change the case">
              {MODEL_FIX_NOTE}
            </Alert>
          )}

          <Collapsible.Root>
            <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
              What we attach
            </Collapsible.Trigger>
            <Collapsible.Panel>
              <ul className="text-fg-muted mt-2 flex list-disc flex-col gap-0.5 pl-5 text-xs">
                {attachedContext(target, location.pathname, form.includeMailText).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <Typography variant="caption" tone="subtle" className="mt-1 block">
                {NOTHING_LEAVES_NOTE}
              </Typography>
            </Collapsible.Panel>
          </Collapsible.Root>
        </>
      )}

      {failure && (
        <Alert tone="warning" title="Not saved">
          {failure}
        </Alert>
      )}

      {asking && typed ? (
        <Alert tone="warning" title={DRAFT_WORDS.askTitle} data-feedback-ask>
          <span className="flex flex-col gap-2">
            <span>{caseId ? DRAFT_WORDS.askOnCase : DRAFT_WORDS.askNoCase}</span>
            <span className="flex flex-wrap justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                autoFocus
                onClick={() => setAsking(false)}
              >
                {DRAFT_WORDS.keepWriting}
              </Button>
              {caseId && (
                <Button size="sm" variant="outline" tone="neutral" onClick={onCancel}>
                  {DRAFT_WORDS.closeKeep}
                </Button>
              )}
              <Button size="sm" variant="outline" tone="danger" onClick={discard}>
                {DRAFT_WORDS.discard}
              </Button>
            </span>
          </span>
        </Alert>
      ) : (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="ghost" tone="neutral" onClick={cancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            tone="accent"
            disabled={!kind || pickingKind}
            loading={create.isPending}
            onClick={save}
          >
            Save feedback
          </Button>
        </div>
      )}
    </div>
  );
}

export interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: GiveFeedbackOptions;
}

/** The DS dialog around the form. Mount it keyed per opening so each starts fresh. */
export function FeedbackDialog({ open, onOpenChange, target }: FeedbackDialogProps) {
  const toast = useToastManager();
  // The usage log: opened, saved, or closed without a save. Never the feedback text.
  const usage = useDialogUsage("give_feedback", open, target.caseId || undefined);
  // Escape, the close button and a click beside the dialog ask first while the form
  // holds typed text (D7); the choice in the ask closes through `onOpenChange` itself.
  const [typed, setTyped] = useState(false);
  const [asking, setAsking] = useState(false);
  const requestOpenChange = (next: boolean) => {
    if (!next && typed) {
      logger.debug("[feedback] close asks first", { case: target.caseId ?? null });
      setAsking(true);
      return;
    }
    onOpenChange(next);
  };
  return (
    <Dialog.Root open={open} onOpenChange={requestOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md">
          <Dialog.Header>
            <Dialog.Title>
              <span className="text-fg inline-flex items-center gap-2">
                <Icon icon={MessageSquarePlus} size="sm" />
                <Typography as="span" variant="heading-6">
                  Give feedback
                </Typography>
              </span>
            </Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex max-h-[75vh] flex-col gap-3 overflow-y-auto">
            <Dialog.Description>
              <Typography variant="body-sm" tone="muted">
                Tell us what is wrong, missing or could be better. You can follow what happens to it
                under My feedback.
              </Typography>
            </Dialog.Description>
            <FeedbackForm
              target={target}
              asking={asking}
              onAskingChange={setAsking}
              onTypedChange={setTyped}
              onCancel={() => onOpenChange(false)}
              onSaved={(item) => {
                toast.add({ title: "Feedback saved", description: SAVED_TOAST, type: "success" });
                usage.saved();
                target.onSaved?.(item);
                onOpenChange(false);
              }}
            />
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
